/**
 * VehicleAssignment
 * Tilldela förare till fordon: ledigt fordon, koppla enhet till befintligt fordon,
 * eller registrera nytt fordon + enhet.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from '@/store/languageStore';
import { useAuth } from '@/store/authStore';
import { useConfirmDialog } from '@/hooks/useConfirmDialog';
import { driverService, AssignmentRow, TripOnDate, ScheduleEntry } from '@/services/drivers/driverService';
import { vehiclesService, type Vehicle } from '@/services/vehicles/vehiclesService';
import { organizationsService } from '@/services/organizations/organizationsService';
import { buildWeekdayServiceScheduleFromOrg } from '@/utils/buildDriverScheduleFromOrgDefaults';
import { resolveFuelTypeFromCarInfo } from '@/utils/resolveFuelTypeFromCarInfo';
import type { VehicleAssignmentDraft } from '@/types/driverSetupDraft';
import api from '@/services/api/client';
import toast from 'react-hot-toast';
import { TruckIcon, CalendarIcon, ArrowRightIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import DateInput from '@/components/common/DateInput';

interface VehicleAssignmentProps {
  driverId?: number;
  organizationId?: string;
  readOnly?: boolean;
  draftMode?: boolean;
  onDraftChange?: (draft: VehicleAssignmentDraft | null) => void;
}

type AssignmentMode = 'pick_unassigned' | 'link_device' | 'register_new';

interface VehicleOption {
  id: string;
  vehicle_id?: number;
  registration_number: string;
  brand?: string;
  make?: string;
  model?: string;
  current_driver_id?: string | number | null;
  device_imei?: string | null;
}

interface DeviceOption {
  imei: string;
  model?: string;
  organization_name?: string;
  status?: string;
}

function vehicleHasActiveDriver(v: VehicleOption): boolean {
  const cid = v.current_driver_id;
  return cid !== null && cid !== undefined && cid !== '';
}

async function applyOrgScheduleAfterAssign(
  driverId: number,
  orgId: string | undefined,
  translate: (key: string) => string
): Promise<void> {
  if (!orgId) return;
  try {
    const data = await organizationsService.getDrivingLogDefaults(orgId);
    const entries: ScheduleEntry[] = buildWeekdayServiceScheduleFromOrg(data.driving_log_settings);
    await driverService.setDriverSchedule(driverId, { mode: 'work_hours', schedule: entries });
    toast.success(translate('drivers.assignment.scheduleApplied'));
  } catch {
    toast(translate('drivers.assignment.scheduleApplyWarn'), { icon: '⚠️' });
  }
}

const VehicleAssignment: React.FC<VehicleAssignmentProps> = ({
  driverId,
  organizationId,
  readOnly = false,
  draftMode = false,
  onDraftChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const ga = user?.is_global_admin;
  const isGlobalAdmin = Boolean(ga === true || ga === 1 || ga === '1');
  const { confirm, ConfirmDialog } = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignments, setAssignments] = useState<AssignmentRow[]>([]);
  const [assignmentMode, setAssignmentMode] = useState<AssignmentMode>('pick_unassigned');

  // Vehicle search (ledigt fordon)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<VehicleOption[]>([]);
  const [allOrgVehicles, setAllOrgVehicles] = useState<VehicleOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);

  // Ny tilldelning: datum + resa
  const [startDate, setStartDate] = useState<string>('');
  const [tripsOnDate, setTripsOnDate] = useState<TripOnDate[]>([]);
  const [selectedTripId, setSelectedTripId] = useState<string>('');
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [currentDriverInfo, setCurrentDriverInfo] = useState<AssignmentRow | null>(null);

  // Koppla enhet
  const [linkVehicleQuery, setLinkVehicleQuery] = useState('');
  const [linkVehicleResults, setLinkVehicleResults] = useState<VehicleOption[]>([]);
  const [linkTargetVehicle, setLinkTargetVehicle] = useState<VehicleOption | null>(null);
  const linkVehicleRef = useRef<HTMLDivElement>(null);
  const [showLinkVehicleDd, setShowLinkVehicleDd] = useState(false);

  const [deviceSearch, setDeviceSearch] = useState('');
  const [deviceResults, setDeviceResults] = useState<DeviceOption[]>([]);
  const [selectedDeviceImei, setSelectedDeviceImei] = useState<string | null>(null);
  const [showDeviceDd, setShowDeviceDd] = useState(false);
  const deviceRef = useRef<HTMLDivElement>(null);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceGlobalSearch, setDeviceGlobalSearch] = useState(false);
  const [handleExistingDevice, setHandleExistingDevice] = useState<'UNMOUNT' | 'SWAP'>('UNMOUNT');
  const [linkStartDate, setLinkStartDate] = useState('');

  // Registrera nytt
  const [regNumber, setRegNumber] = useState('');
  const [regVin, setRegVin] = useState('');
  const [regMake, setRegMake] = useState('');
  const [regModel, setRegModel] = useState('');
  const [regYear, setRegYear] = useState<number | ''>('');
  const [regFuel, setRegFuel] = useState<string>('');
  const [regFetching, setRegFetching] = useState(false);
  const [regCarInfoOk, setRegCarInfoOk] = useState(false);
  const [regInitialOdometerKm, setRegInitialOdometerKm] = useState<number | ''>('');
  const [regDeviceImei, setRegDeviceImei] = useState<string | null>(null);
  const [regDeviceSearch, setRegDeviceSearch] = useState('');
  const [regDeviceResults, setRegDeviceResults] = useState<DeviceOption[]>([]);
  const [showRegDeviceDd, setShowRegDeviceDd] = useState(false);
  const regDeviceRef = useRef<HTMLDivElement>(null);
  const [regStartDate, setRegStartDate] = useState('');
  const [regDeviceGlobal, setRegDeviceGlobal] = useState(false);

  const loadAssignments = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    try {
      const data = await driverService.getAssignments(driverId);
      setAssignments(data);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [driverId, t]);

  const loadVehicles = useCallback(async () => {
    try {
      const result = await vehiclesService.getVehicles({
        organizationId: organizationId || undefined,
        includeSubOrgs: true,
        limit: 10000,
      });
      setAllOrgVehicles(result.vehicles as VehicleOption[]);
    } catch {
      try {
        const fallbackResult = await vehiclesService.getVehicles({ limit: 10000, includeSubOrgs: true });
        setAllOrgVehicles(fallbackResult.vehicles as VehicleOption[]);
      } catch {
        /* ignore */
      }
    }
  }, [organizationId]);

  useEffect(() => {
    loadAssignments();
    loadVehicles();
  }, [loadAssignments, loadVehicles]);

  const assignableVehicles = useMemo(
    () => allOrgVehicles.filter((v) => !vehicleHasActiveDriver(v)),
    [allOrgVehicles]
  );

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(assignableVehicles.slice(0, 20));
      return;
    }
    const query = searchQuery.toLowerCase().trim();
    const filtered = assignableVehicles
      .filter((v) => {
        const reg = (v.registration_number || '').toLowerCase();
        const brand = (v.brand || v.make || '').toLowerCase();
        const model = (v.model || '').toLowerCase();
        return reg.includes(query) || brand.includes(query) || model.includes(query);
      })
      .slice(0, 30);
    setSearchResults(filtered);
  }, [searchQuery, assignableVehicles]);

  useEffect(() => {
    if (!linkVehicleQuery.trim()) {
      setLinkVehicleResults(allOrgVehicles.slice(0, 25));
      return;
    }
    const q = linkVehicleQuery.toLowerCase().trim();
    setLinkVehicleResults(
      allOrgVehicles
        .filter((v) => {
          const reg = (v.registration_number || '').toLowerCase();
          const brand = (v.brand || v.make || '').toLowerCase();
          return reg.includes(q) || brand.includes(q);
        })
        .slice(0, 40)
    );
  }, [linkVehicleQuery, allOrgVehicles]);

  const fetchUnassignedDevices = useCallback(
    async (search: string, forRegister: boolean) => {
      setLoadingDevices(true);
      try {
        const useGlobal = forRegister ? regDeviceGlobal && isGlobalAdmin : deviceGlobalSearch && isGlobalAdmin;
        let raw: DeviceOption[] = [];

        if (useGlobal) {
          const params = new URLSearchParams();
          if (search) params.set('search', search);
          params.set('limit', '0');
          params.set('onlyWithoutVehicle', 'true');
          params.set('unassignedGlobal', 'true');
          const res = await api.get(`/api/device-management/devices?${params}`);
          raw = Array.isArray(res.data.data) ? res.data.data : [];
        } else if (organizationId) {
          const params = new URLSearchParams();
          params.set('organizationId', String(organizationId));
          if (search) params.set('search', search);
          const res = await api.get(`/api/vehicles/available-devices?${params}`);
          raw = Array.isArray(res.data.data) ? res.data.data : [];
        }

        const filtered = raw.filter((d) => d.status !== 'VEHICLE');
        if (forRegister) setRegDeviceResults(filtered.slice(0, 50));
        else setDeviceResults(filtered.slice(0, 50));
      } catch {
        if (forRegister) setRegDeviceResults([]);
        else setDeviceResults([]);
      } finally {
        setLoadingDevices(false);
      }
    },
    [organizationId, isGlobalAdmin, deviceGlobalSearch, regDeviceGlobal]
  );

  useEffect(() => {
    if (assignmentMode !== 'link_device' && assignmentMode !== 'register_new') return;
    const tmr = setTimeout(() => {
      if (assignmentMode === 'link_device') fetchUnassignedDevices(deviceSearch, false);
      else fetchUnassignedDevices(regDeviceSearch, true);
    }, 300);
    return () => clearTimeout(tmr);
  }, [deviceSearch, regDeviceSearch, assignmentMode, fetchUnassignedDevices]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowDropdown(false);
      if (linkVehicleRef.current && !linkVehicleRef.current.contains(e.target as Node)) setShowLinkVehicleDd(false);
      if (deviceRef.current && !deviceRef.current.contains(e.target as Node)) setShowDeviceDd(false);
      if (regDeviceRef.current && !regDeviceRef.current.contains(e.target as Node)) setShowRegDeviceDd(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    const loadTrips = async () => {
      if (!selectedVehicle || !startDate || assignmentMode !== 'pick_unassigned') {
        setTripsOnDate([]);
        setCurrentDriverInfo(null);
        return;
      }
      setLoadingTrips(true);
      try {
        const vId = parseInt(String(selectedVehicle.vehicle_id || selectedVehicle.id), 10);
        const [trips, currentDriver] = await Promise.all([
          driverService.getTripsOnDate(vId, startDate),
          driverService.getCurrentDriver(vId),
        ]);
        setTripsOnDate(trips);
        setCurrentDriverInfo(currentDriver);
      } catch {
        /* ignore */
      } finally {
        setLoadingTrips(false);
      }
    };
    loadTrips();
  }, [selectedVehicle, startDate, assignmentMode]);

  const tryApplyOrgSchedule = async () => {
    if (!driverId) return;
    await applyOrgScheduleAfterAssign(driverId, organizationId, t);
  };

  const handleAssign = async () => {
    if (!selectedVehicle || !startDate) return;
    const vehicleId = parseInt(String(selectedVehicle.vehicle_id || selectedVehicle.id), 10);
    const startDateIso = selectedTripId
      ? tripsOnDate.find((trip) => trip.trip_id === selectedTripId)?.start_time || `${startDate}T00:00:00`
      : `${startDate}T00:00:00`;

    if (draftMode && onDraftChange) {
      onDraftChange({
        kind: 'pick',
        vehicleId,
        startDate: startDateIso,
        startTripId: selectedTripId || undefined,
      });
      setSuccess(t('userFormFields.vehicleDraftSaved'));
      setTimeout(() => setSuccess(null), 3000);
      return;
    }
    if (!driverId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await driverService.assignDriverToVehicle({
        driverId,
        vehicleId,
        startDate: startDateIso,
        startTripId: selectedTripId || undefined,
      });
      await tryApplyOrgSchedule();
      setSuccess(t('drivers.assignment.assignSuccess'));
      setSelectedVehicle(null);
      setSearchQuery('');
      setStartDate('');
      setSelectedTripId('');
      setTripsOnDate([]);
      loadAssignments();
      loadVehicles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleLinkDeviceAndAssign = async () => {
    if (!organizationId) {
      toast.error(t('drivers.assignment.orgRequired'));
      return;
    }
    if (!linkTargetVehicle || !selectedDeviceImei || !linkStartDate) {
      toast.error(t('common.error'));
      return;
    }
    const vehicleId = parseInt(String(linkTargetVehicle.vehicle_id || linkTargetVehicle.id), 10);

    if (draftMode && onDraftChange) {
      const hasDevice = Boolean(linkTargetVehicle.device_imei);
      onDraftChange({
        kind: 'link',
        vehicleId,
        deviceImei: selectedDeviceImei,
        startDate: `${linkStartDate}T00:00:00`,
        handleExistingDevice: hasDevice ? handleExistingDevice : 'UNMOUNT',
      });
      setSuccess(t('userFormFields.vehicleDraftSaved'));
      setTimeout(() => setSuccess(null), 3000);
      return;
    }
    if (!driverId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const hasDevice = Boolean(linkTargetVehicle.device_imei);
      await api.post(`/api/device-management/devices/${selectedDeviceImei}/move`, {
        targetVehicleId: vehicleId,
        handleExistingDevice: hasDevice ? handleExistingDevice : undefined,
        notes: t('copy.ui.linkedFromDriverForm'),
        // Backdatera monteringen så device_vehicle_history matchar förarkopplingens start
        startDate: `${linkStartDate}T00:00:00`,
      });
      await driverService.assignDriverToVehicle({
        driverId,
        vehicleId,
        startDate: `${linkStartDate}T00:00:00`,
      });
      await tryApplyOrgSchedule();
      setSuccess(t('drivers.assignment.linkDeviceSuccess'));
      setLinkTargetVehicle(null);
      setLinkVehicleQuery('');
      setSelectedDeviceImei(null);
      setDeviceSearch('');
      setLinkStartDate('');
      loadAssignments();
      loadVehicles();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleFetchCarInfo = async () => {
    if (!regNumber.trim()) {
      toast.error(t('fleet.addVehicle.enterRegNumber'));
      return;
    }
    setRegFetching(true);
    try {
      const response = await api.get<{
        success: boolean;
        fuelType?: string | null;
        specifications?: unknown[];
        data: Record<string, unknown>;
      }>(
        `/api/carinfo/lookup/${regNumber.trim()}`
      );
      if (response.data.success && response.data.data) {
        const data = response.data.data as Record<string, unknown>;
        if (data.vin) setRegVin(String(data.vin));
        if (data.brand) setRegMake(String(data.brand));
        if (data.model) setRegModel(String(data.model));
        if (data.modelYear) setRegYear(Number(data.modelYear));
        if (data.registrationNumber) setRegNumber(String(data.registrationNumber));
        const mappedFuel =
          response.data.fuelType ??
          resolveFuelTypeFromCarInfo({ specifications: response.data.specifications });
        if (mappedFuel) setRegFuel(mappedFuel);
        setRegCarInfoOk(true);
        toast.success(t('fleet.addVehicle.carInfoSuccess'));
      } else {
        toast.error(t('fleet.addVehicle.carInfoNotFound'));
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setRegFetching(false);
    }
  };

  const handleRegisterVehicleAndAssign = async () => {
    if (!organizationId) {
      toast.error(t('drivers.assignment.orgRequired'));
      return;
    }
    if (!regNumber.trim() || !regStartDate) {
      toast.error(t('common.error'));
      return;
    }

    if (draftMode && onDraftChange) {
      const cleanReg = regNumber.trim().toUpperCase().replace(/\s/g, '');
      onDraftChange({
        kind: 'register',
        startDate: `${regStartDate}T00:00:00`,
        deviceImei: regDeviceImei,
        vehicle: {
          registration_number: cleanReg,
          vin: regVin.trim() || undefined,
          make: regMake.trim() || undefined,
          model: regModel.trim() || undefined,
          year: regYear || undefined,
          fuel_type: regFuel || undefined,
          initial_odometer:
            regInitialOdometerKm !== '' && regInitialOdometerKm != null
              ? Number(regInitialOdometerKm) * 1000
              : undefined,
        },
      });
      setSuccess(t('userFormFields.vehicleDraftSaved'));
      setTimeout(() => setSuccess(null), 3000);
      return;
    }
    if (!driverId) return;

    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const cleanReg = regNumber.trim().toUpperCase().replace(/\s/g, '');
      const vehicleData = {
        registration_number: cleanReg,
        vin: regVin.trim() || undefined,
        make: regMake.trim() || undefined,
        model: regModel.trim() || undefined,
        year: regYear || undefined,
        fuel_type: regFuel || undefined,
        status: 'ACTIVE',
        organization_id: organizationId,
        initial_odometer: regInitialOdometerKm !== '' && regInitialOdometerKm != null ? Number(regInitialOdometerKm) * 1000 : undefined,
        is_poolcar: false,
      };
      const res = await api.post<{ success: boolean; data: Vehicle; error?: string }>('/api/vehicles', vehicleData);
      if (!res.data.success || !res.data.data) {
        throw new Error(res.data.error || 'create failed');
      }
      const created = res.data.data as Vehicle & { vehicle_id?: number };
      const newVehicleId = parseInt(String(created.vehicle_id ?? created.id), 10);
      if (regDeviceImei) {
        await api.post(`/api/device-management/devices/${regDeviceImei}/move`, {
          targetVehicleId: newVehicleId,
          notes: t('copy.ui.linkedOnDriverCreate'),
          // Backdatera monteringen så device_vehicle_history matchar förarkopplingens start
          startDate: `${regStartDate}T00:00:00`,
        });
      }
      await driverService.assignDriverToVehicle({
        driverId,
        vehicleId: newVehicleId,
        startDate: `${regStartDate}T00:00:00`,
      });
      await tryApplyOrgSchedule();
      setSuccess(t('drivers.assignment.registerSuccess'));
      setRegNumber('');
      setRegVin('');
      setRegMake('');
      setRegModel('');
      setRegYear('');
      setRegFuel('');
      setRegCarInfoOk(false);
      setRegInitialOdometerKm('');
      setRegDeviceImei(null);
      setRegStartDate('');
      loadAssignments();
      loadVehicles();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleEndAssignment = async (assignment: AssignmentRow) => {
    try {
      await driverService.endAssignment(assignment.id, new Date().toISOString());
      setSuccess(t('drivers.assignment.endSuccess'));
      loadAssignments();
      loadVehicles();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.error'));
    }
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('sv-SE', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activeAssignment = assignments.find((a) => !a.end_date);

  const modeRadios = (
    <div className="flex flex-wrap gap-3 text-xs">
      {(['pick_unassigned', 'link_device', 'register_new'] as AssignmentMode[]).map((m) => (
        <label key={m} className="flex items-center gap-1.5 cursor-pointer">
          <input
            type="radio"
            name="assignMode"
            checked={assignmentMode === m}
            onChange={() => {
              setAssignmentMode(m);
              setError(null);
            }}
            className="text-primary"
          />
          <span>
            {m === 'pick_unassigned' && t('drivers.assignment.modePickUnassigned')}
            {m === 'link_device' && t('drivers.assignment.modeLinkDevice')}
            {m === 'register_new' && t('drivers.assignment.modeRegisterNew')}
          </span>
        </label>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-2">
        <TruckIcon className="w-5 h-5 text-textSecondary" />
        <h4 className="text-sm font-semibold text-textDefault">{t('drivers.assignment.title')}</h4>
      </div>

      {draftMode && (
        <p className="text-xs text-primary/90 bg-BgPrimaryLight/20 border border-primary/20 rounded-lg px-3 py-2">
          {t('userFormFields.driverDraftHint')}
        </p>
      )}

      {!readOnly && modeRadios}
      {!readOnly && (
        <p className="text-xs text-textTertiary">
          {assignmentMode === 'pick_unassigned' && t('drivers.assignment.modeHelpPick')}
          {assignmentMode === 'link_device' && t('drivers.assignment.modeHelpLink')}
          {assignmentMode === 'register_new' && t('drivers.assignment.modeHelpRegister')}
        </p>
      )}

      {success && (
        <div className="p-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded text-green-700 dark:text-green-300 text-xs">
          {success}
        </div>
      )}
      {error && (
        <div className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-red-700 dark:text-red-300 text-xs">
          {error}
        </div>
      )}

      {activeAssignment && (
        <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">{t('drivers.assignment.currentVehicle')}</p>
          <p className="text-sm font-semibold text-textDefault">
            {activeAssignment.vehicle_registration || `Vehicle ${activeAssignment.vehicle_id}`}
          </p>
          <p className="text-xs text-textTertiary">
            {t('drivers.assignment.since')} {formatDateTime(activeAssignment.start_date)}
          </p>
          {!readOnly && (
            <button
              type="button"
              onClick={() =>
                confirm({
                  title: t('common.confirm'),
                  message: t('drivers.assignment.confirmEnd') ?? '',
                  onConfirm: () => handleEndAssignment(activeAssignment),
                  variant: 'warning',
                })
              }
              className="mt-2 text-xs text-red-600 hover:underline"
            >
              {t('drivers.assignment.endAssignment')}
            </button>
          )}
        </div>
      )}

      {!readOnly && assignmentMode === 'pick_unassigned' && (
        <div className="space-y-3 p-3 bg-bgLight/30 rounded-lg">
          <p className="text-xs font-medium text-textSecondary">{t('drivers.assignment.newAssignment')}</p>

          <div ref={searchRef} className="relative">
            <label className="block text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.selectVehicle')}</label>
            {selectedVehicle ? (
              <div className="flex items-center gap-2 px-3 py-2 border border-primary/30 bg-BgPrimaryLight/20 rounded-lg">
                <TruckIcon className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-textDefault flex-1">{selectedVehicle.registration_number}</span>
                <button type="button" onClick={() => { setSelectedVehicle(null); setSearchQuery(''); }} className="text-textTertiary hover:text-textDefault">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-textTertiary" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setShowDropdown(true)}
                  placeholder={t('drivers.assignment.searchVehicle')}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault focus:ring-primary focus:border-primary"
                />
              </div>
            )}
            {showDropdown && !selectedVehicle && (
              <div className="absolute z-50 mt-1 w-full bg-bgElevated border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchResults.length === 0 ? (
                  <p className="px-3 py-2 text-xs text-textTertiary italic">
                    {searchQuery.trim()
                      ? t('drivers.assignment.noVehiclesFound')
                      : assignableVehicles.length === 0
                        ? t('drivers.assignment.noUnassignedVehicles')
                        : t('drivers.assignment.typeToSearch')}
                  </p>
                ) : (
                  searchResults.map((v) => (
                    <button
                      key={v.id || v.vehicle_id}
                      type="button"
                      onClick={() => {
                        setSelectedVehicle(v);
                        setSearchQuery('');
                        setShowDropdown(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-bgLight transition-colors"
                    >
                      <span className="font-medium">{v.registration_number}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {currentDriverInfo && (
            <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 rounded text-yellow-800 text-xs">
              {t('drivers.assignment.currentDriverWarning', {
                name: currentDriverInfo.driver_name || `Driver ${currentDriverInfo.driver_id}`,
              })}
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-textSecondary mb-1">
              <CalendarIcon className="inline w-4 h-4 mr-1" />
              {t('drivers.assignment.selectDate')}
            </label>
            <DateInput value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated text-textDefault"
            />
          </div>

          {selectedVehicle && startDate && (
            <div>
              <p className="text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.selectTrip')}</p>
              {loadingTrips ? (
                <p className="text-xs text-textTertiary">{t('common.loading')}</p>
              ) : tripsOnDate.length === 0 ? (
                <p className="text-xs text-textTertiary italic">{t('drivers.assignment.noTrips')}</p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {tripsOnDate.map((trip) => (
                    <button
                      key={trip.trip_id}
                      type="button"
                      onClick={() => setSelectedTripId(selectedTripId === trip.trip_id ? '' : trip.trip_id)}
                      className={`w-full text-left p-2 rounded text-xs transition-colors ${
                        selectedTripId === trip.trip_id ? 'bg-primary/10 border border-primary/30' : 'bg-bgElevated hover:bg-bgLight border border-transparent'
                      }`}
                    >
                      <span className="font-medium">{formatTime(trip.start_time)}</span>
                      <ArrowRightIcon className="inline w-3 h-3 mx-1" />
                      <span>{formatTime(trip.end_time)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleAssign}
            disabled={saving || !selectedVehicle || !startDate}
            className="w-full px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('drivers.assignment.assign')}
          </button>
        </div>
      )}

      {!readOnly && assignmentMode === 'link_device' && (
        <div className="space-y-3 p-3 bg-bgLight/30 rounded-lg border border-border">
          {!organizationId && <p className="text-xs text-amber-700">{t('drivers.assignment.orgRequired')}</p>}
          <div ref={linkVehicleRef} className="relative">
            <label className="block text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.searchVehicleInOrg')}</label>
            {linkTargetVehicle ? (
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg bg-bgElevated">
                <span className="text-sm font-medium">{linkTargetVehicle.registration_number}</span>
                {linkTargetVehicle.device_imei && (
                  <span className="text-xs text-textTertiary">GPS: {linkTargetVehicle.device_imei}</span>
                )}
                <button type="button" onClick={() => setLinkTargetVehicle(null)} className="ml-auto">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={linkVehicleQuery}
                  onChange={(e) => { setLinkVehicleQuery(e.target.value); setShowLinkVehicleDd(true); }}
                  onFocus={() => setShowLinkVehicleDd(true)}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated"
                  placeholder={t('drivers.assignment.searchVehicle')}
                />
                {showLinkVehicleDd && (
                  <div className="absolute z-50 mt-1 w-full max-h-44 overflow-y-auto bg-bgElevated border border-border rounded-lg shadow">
                    {linkVehicleResults.map((v) => (
                      <button
                        key={String(v.id)}
                        type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-bgLight"
                        onClick={() => {
                          setLinkTargetVehicle(v);
                          setShowLinkVehicleDd(false);
                        }}
                      >
                        {v.registration_number}
                        {v.device_imei ? ` · GPS` : ''}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {linkTargetVehicle?.device_imei && (
            <div className="space-y-2">
              <p className="text-xs font-medium">{t('drivers.assignment.existingDevicePolicy')}</p>
              <label className="flex items-center gap-2 text-xs">
                <input type="radio" checked={handleExistingDevice === 'UNMOUNT'} onChange={() => setHandleExistingDevice('UNMOUNT')} />
                {t('drivers.assignment.unmountExisting')}
              </label>
              <label className="flex items-center gap-2 text-xs">
                <input type="radio" checked={handleExistingDevice === 'SWAP'} onChange={() => setHandleExistingDevice('SWAP')} />
                {t('drivers.assignment.swapDevices')}
              </label>
            </div>
          )}

          <div ref={deviceRef} className="relative">
            <label className="block text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.selectDevice')}</label>
            {isGlobalAdmin && (
              <label className="flex items-center gap-2 text-xs mb-1">
                <input type="checkbox" checked={deviceGlobalSearch} onChange={(e) => setDeviceGlobalSearch(e.target.checked)} />
                {t('drivers.assignment.deviceGlobalSearch')}
              </label>
            )}
            {selectedDeviceImei ? (
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg">
                <span className="text-sm font-mono">{selectedDeviceImei}</span>
                <button type="button" className="ml-auto" onClick={() => setSelectedDeviceImei(null)}>
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={deviceSearch}
                  onChange={(e) => { setDeviceSearch(e.target.value); setShowDeviceDd(true); }}
                  onFocus={() => setShowDeviceDd(true)}
                  placeholder={t('drivers.assignment.searchDevice')}
                  className="w-full px-3 py-2 text-sm border border-border rounded-lg bg-bgElevated font-mono"
                />
                {showDeviceDd && (
                  <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-bgElevated border rounded-lg shadow text-xs">
                    {loadingDevices ? (
                      <p className="p-2">{t('common.loading')}</p>
                    ) : deviceResults.length === 0 ? (
                      <p className="p-2 text-textTertiary">{t('drivers.assignment.noDevicesFound')}</p>
                    ) : (
                      deviceResults.map((d) => (
                        <button
                          key={d.imei}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-bgLight"
                          onClick={() => {
                            setSelectedDeviceImei(d.imei);
                            setShowDeviceDd(false);
                          }}
                        >
                          {d.imei}
                          {d.organization_name ? ` · ${d.organization_name}` : ''}
                        </button>
                      ))
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.selectDate')}</label>
            <DateInput value={linkStartDate} onChange={(e) => setLinkStartDate(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg bg-bgElevated" />
          </div>

          <button
            type="button"
            onClick={handleLinkDeviceAndAssign}
            disabled={saving || !linkTargetVehicle || !selectedDeviceImei || !linkStartDate || !organizationId}
            className="w-full py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('drivers.assignment.linkDeviceAndAssign')}
          </button>
        </div>
      )}

      {!readOnly && assignmentMode === 'register_new' && (
        <div className="space-y-3 p-3 bg-bgLight/30 rounded-lg border border-border">
          {!organizationId && <p className="text-xs text-amber-700">{t('drivers.assignment.orgRequired')}</p>}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-xs text-textSecondary mb-1">{t('drivers.assignment.regLabel')}</label>
              <input
                value={regNumber}
                onChange={(e) => setRegNumber(e.target.value)}
                placeholder={t('drivers.assignment.registerRegPlaceholder')}
                className="w-full px-3 py-2 text-sm border rounded-lg bg-bgElevated uppercase"
              />
            </div>
            <button
              type="button"
              onClick={handleFetchCarInfo}
              disabled={regFetching}
              className="px-3 py-2 text-sm bg-bgElevated border border-border rounded-lg hover:bg-bgLight"
            >
              {regFetching ? t('common.loading') : t('drivers.assignment.fetchCarInfo')}
            </button>
          </div>
          {regCarInfoOk && (
            <div className="grid grid-cols-2 gap-2 text-xs">
              <input value={regMake} onChange={(e) => setRegMake(e.target.value)} placeholder={t('drivers.assignment.makePlaceholder')} className="px-2 py-1 border rounded" />
              <input value={regModel} onChange={(e) => setRegModel(e.target.value)} placeholder={t('drivers.assignment.modelPlaceholder')} className="px-2 py-1 border rounded" />
              <input value={regVin} onChange={(e) => setRegVin(e.target.value)} placeholder="VIN" className="px-2 py-1 border rounded col-span-2" />
              <input
                type="number"
                value={regYear === '' ? '' : regYear}
                onChange={(e) => setRegYear(e.target.value === '' ? '' : parseInt(e.target.value, 10))}
                placeholder={t('drivers.assignment.yearPlaceholder')}
                className="px-2 py-1 border rounded"
              />
              <select value={regFuel} onChange={(e) => setRegFuel(e.target.value)} className="px-2 py-1 border rounded">
                <option value="">{t('drivers.assignment.fuelPlaceholder')}</option>
                <option value="PETROL">{t('drivers.assignment.fuelPetrol')}</option>
                <option value="DIESEL">{t('drivers.assignment.fuelDiesel')}</option>
                <option value="ELECTRIC">{t('drivers.assignment.fuelElectric')}</option>
                <option value="HYBRID">{t('drivers.assignment.fuelHybrid')}</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-textSecondary mb-1">{t('drivers.assignment.initialOdometerKm')}</label>
            <input
              type="number"
              min={0}
              value={regInitialOdometerKm === '' ? '' : regInitialOdometerKm}
              onChange={(e) => setRegInitialOdometerKm(e.target.value === '' ? '' : parseFloat(e.target.value))}
              className="w-full max-w-xs px-3 py-2 text-sm border rounded-lg bg-bgElevated"
            />
          </div>
          <div ref={regDeviceRef} className="relative">
            <label className="block text-xs font-medium text-textSecondary mb-1">{t('drivers.assignment.selectDevice')}</label>
            {isGlobalAdmin && (
              <label className="flex items-center gap-2 text-xs mb-1">
                <input type="checkbox" checked={regDeviceGlobal} onChange={(e) => setRegDeviceGlobal(e.target.checked)} />
                {t('drivers.assignment.deviceGlobalSearch')}
              </label>
            )}
            {regDeviceImei ? (
              <div className="flex items-center gap-2 px-3 py-2 border rounded-lg font-mono text-sm">
                {regDeviceImei}
                <button type="button" className="ml-auto" onClick={() => setRegDeviceImei(null)}>
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <input
                  value={regDeviceSearch}
                  onChange={(e) => { setRegDeviceSearch(e.target.value); setShowRegDeviceDd(true); }}
                  onFocus={() => setShowRegDeviceDd(true)}
                  placeholder={t('drivers.assignment.searchDevice')}
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-bgElevated font-mono"
                />
                {showRegDeviceDd && (
                  <div className="absolute z-50 mt-1 w-full max-h-40 overflow-y-auto bg-bgElevated border rounded-lg shadow text-xs">
                    {regDeviceResults.map((d) => (
                      <button
                        key={d.imei}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-bgLight"
                        onClick={() => {
                          setRegDeviceImei(d.imei);
                          setShowRegDeviceDd(false);
                        }}
                      >
                        {d.imei}
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <div>
            <label className="block text-xs text-textSecondary mb-1">{t('drivers.assignment.selectDate')}</label>
            <DateInput value={regStartDate} onChange={(e) => setRegStartDate(e.target.value)} className="w-full px-3 py-2 text-sm border rounded-lg bg-bgElevated" />
          </div>
          <button
            type="button"
            onClick={handleRegisterVehicleAndAssign}
            disabled={saving || !regNumber.trim() || !regStartDate || !organizationId}
            className="w-full py-2 text-sm bg-primary text-white rounded-lg disabled:opacity-50"
          >
            {saving ? t('common.saving') : t('drivers.assignment.createVehicleAndAssign')}
          </button>
        </div>
      )}

      {assignments.length > 0 && (
        <div>
          <p className="text-xs font-medium text-textSecondary mb-2">{t('drivers.assignment.history')}</p>
          <div className="space-y-1">
            {assignments.map((a) => (
              <div key={a.id} className="flex items-center gap-2 p-2 bg-bgLight/30 rounded text-xs">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${a.end_date ? 'bg-gray-400' : 'bg-green-500'}`} />
                <span className="font-medium">{a.vehicle_registration || `Vehicle ${a.vehicle_id}`}</span>
                <span className="text-textTertiary">
                  {formatDateTime(a.start_date)}
                  {a.end_date && ` - ${formatDateTime(a.end_date)}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && <p className="text-xs text-textTertiary">{t('common.loading')}</p>}

      <ConfirmDialog />
    </div>
  );
};

export default VehicleAssignment;
